import { useContext, useEffect, useMemo, useState } from 'react';

import { useStatsigClient } from '@statsig/react-bindings';

import { SessionContext } from '../../context/auth/authContext';
import { CompanyContext } from '../../context/company/companyContext';
import { PlansContext } from '../../context/plans/plansContext';
import { buildStatsigUser } from '../../utils/statsig/statsigUser';

export interface UseFeatureGateOptions {
  updateUserContext?: boolean;
}

export const useFeatureGate = (gateKey: string, options: UseFeatureGateOptions = {}) => {
  const { client } = useStatsigClient();
  const { company, companyData, holdingList } = useContext(CompanyContext);
  const { email, userId } = useContext(SessionContext);
  const { plans, planType } = useContext(PlansContext);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { updateUserContext = true } = options;

  useEffect(() => {
    if (!updateUserContext || !client || !userId || !company?.externalId || !companyData?.id) {
      return;
    }

    const updateStatsigContext = async () => {
      setIsLoading(true);
      try {
        await client.updateUserAsync(
          buildStatsigUser({ userId, email, holdingList, company, companyData, plans, planType })
        );

        setTimeout(() => {
          setRefreshTrigger((prev) => prev + 1);
          setIsLoading(false);
        }, 200);
      } catch {
        setRefreshTrigger((prev) => prev + 1);
        setIsLoading(false);
      }
    };

    updateStatsigContext();
  }, [
    client,
    userId,
    email,
    holdingList,
    company,
    companyData?.id,
    companyData?.countryCode,
    companyData?.createdAt,
    plans,
    planType,
    updateUserContext
  ]);

  const isEnabled = useMemo(() => {
    if (!client) return false;

    return client.checkGate(gateKey);
  }, [client, gateKey, refreshTrigger]);

  const refresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return {
    isEnabled,
    isLoading,
    refresh
  };
};
